import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { animateScroll as scroll } from 'react-scroll';
import { useQuery } from 'react-apollo';

import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import SignupAlbumLayout from '../../../../components/global/SignupAlbumLayout';
import useOpenAlert from '../../../../hooks/useOpenAlert';
import useOpenModal from '../../../../hooks/useOpenModal';
import useCloseModal from '../../../../hooks/useCloseModal';
import useFreemiumEventCreate from '../../../pro/views/AlbumFolder/FreemiumEventCreateWizard/hooks/useFreemiumEventCreate';
import { CONFIGURE_EVENT_FOR_GIFT_MODAL } from '../WeddingsGift/components/ConfigureEventForGiftModal';
import {
  prepareAlbumForCheckout,
  processAlbumPostCheckout,
} from '../../../pro/views/AlbumFolder/FreemiumEventCreateWizard/helpers';
import { FREEMIUM_EVENTS_META } from '../../../pro/components/FreemiumEventPurchase/helpers';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import useAuthenticated from '../../../../hooks/useAuthenticated';
import { getErrorString } from '../../../../util/normalize-errors';
import FreemiumEventCreateForm from '../../../pro/views/AlbumFolder/FreemiumEventCreateWizard/FreemiumEventCreateForm';
import { FREEMIUM_EVENT_STEPS } from '../../../pro/views/AlbumFolder/FreemiumEventCreateWizard/constants';
import { MeQuery } from '../../../../graphql/queries/account';
import { CONFIGURE_EVENT_FOR_GIFT_SUCCESS_MODAL } from '../WeddingsGift/components/ConfigureEventForGiftSuccessModal';
import { FREEMIUM_EVENT_CREATE_SUCCESS_MODAL } from '../../../pro/components/FreemiumEventCreateSuccess';
import useBasename from '../../../../components/routing/useBasename';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

const WeddingsGiftConfigure = ({ history }) => {
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const basename = useBasename();
  const authenticated = useAuthenticated();
  const [creating, setCreating] = useState(false);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });

  const {
    albumSetup,
    setAlbumSetup,
    onAlbumCreate,
    onAlbumUpdate,
    onAlbumDelete,
    onAlbumFolderUpdate,
    emailExists,
  } = useFreemiumEventCreate({
    onSetupChange: ({ albumSetup: latestSetup }) => {
      if (latestSetup.isPurchased) {
        openModal(CONFIGURE_EVENT_FOR_GIFT_MODAL, {
          albumFolder: latestSetup?.album?.album_folder,
        });
      }
    },
  });

  const onStepChange = async () => {
    // Hack to scroll to the bottom of the screen where we present the errors.
    scroll.scrollToTop({
      delay: 0,
      duration: 700,
      smooth: true,
      isDynamic: true,
    });
  };

  const onCheckoutComplete = async ({
    album,
    values,
    eventType,
    purchasedEventAddons,
    purchasedEventAddonItems,
    includePhotosAlbum,
  }) => {
    const {
      redirectAlbum,
      redirectToAlbumFolder,
      isPurchased,
    } = await processAlbumPostCheckout({
      album,
      values,
      purchasedEventAddons,
      purchasedEventAddonItems,
      eventType,
      user,
      albumCreate: onAlbumCreate,
      albumUpdate: onAlbumUpdate,
      albumDelete: onAlbumDelete,
      albumFolderUpdate: onAlbumFolderUpdate,
      includePhotosAlbum,
    });

    setAlbumSetup({
      isPurchased,
      album: redirectAlbum,
      redirectToAlbumFolder,
    });
    setCreating(false);
  };

  const onFormComplete = async ({
    values,
    formikBag,
    eventType,
    stepProps,
    onEventAddonPurchaseCheckout,
  }) => {
    if (creating) {
      return;
    }

    // If the purchase has been completed, we should not re-charge.
    // We should focus on re-allowing the user to gift.
    if (albumSetup?.isPurchased) {
      openModal(CONFIGURE_EVENT_FOR_GIFT_MODAL, {
        albumFolder: albumSetup?.album?.root_album_folder,
      });
    }

    setCreating(true);
    try {
      const {
        currentSelectedEventAddonItemIds,
        includePhotosAlbum,
      } = stepProps;
      const checkoutRequired = currentSelectedEventAddonItemIds.length > 0;

      const { album } = await prepareAlbumForCheckout({
        values,
        albumId: albumSetup?.album?.id,
        albumCreate: onAlbumCreate,
        albumUpdate: onAlbumUpdate,
        albumFolderUpdate: onAlbumFolderUpdate,
        user,
        eventType,
      });

      if (!checkoutRequired) {
        await onCheckoutComplete({
          album,
          values,
          user,
          includePhotosAlbum,
        });
        return;
      }

      // Perform checkout operations.
      const meta = FREEMIUM_EVENTS_META[eventType];

      openModal(STRIPE_CHECKOUT_MODAL, {
        title: <h3 className="text-center">{meta.title}</h3>,
        buttonLabel: 'Complete payment',
        // If the event is FREE, will not require CC
        cardRequired: false,
        onSuccess: token => {
          setCreating(true);
          onEventAddonPurchaseCheckout({
            token,
            albumFolderId: album.album_folder.id,
            values,
            album,
            formikBag,
            eventType,
            user,
            includePhotosAlbum,
          });
        },
        accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            event_addon_items: currentSelectedEventAddonItemIds.map(id => ({
              event_addon_item_id: id,
              quantity: 1,
              album_folder_id: album.album_folder.id,
            })),
            promo_code: promoCode,
          };
        },
        onClose: () => {
          setCreating(false);
          setAlbumSetup({ album });
          closeModal(STRIPE_CHECKOUT_MODAL);
        },
        defaultName: user?.name ?? '',
        defaultEmail: user?.email_address ?? '',
      });
    } catch (error) {
      openAlert({
        message:
          'There was an error attempting to create the new event. Please try again later.',
        info: getErrorString(error),
      });

      setCreating(false);
    }
  };

  const onCloseGiftSuccessModal = () => {
    closeModal(CONFIGURE_EVENT_FOR_GIFT_SUCCESS_MODAL);
    if (albumSetup.isPurchased && albumSetup.redirectToAlbumFolder) {
      history.push(
        `/app/shutterbug/folders/${albumSetup?.album?.album_folder?.id}`,
      );
    } else if (albumSetup.isPurchased) {
      openModal(FREEMIUM_EVENT_CREATE_SUCCESS_MODAL);
      history.push(`/app/${basename}/albums/${albumSetup?.album?.id}`);
    }
  };

  if (!user) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Create Album</title>
      </Helmet>
      <SignupAlbumLayout.Wrapper logoSrc="/waldowed-logo.svg" withCTA={false}>
        <div className="weddings-gift-configure">
          <FreemiumEventCreateForm
            user={user}
            creating={creating}
            authenticated={authenticated}
            allowFreeAlbums={false}
            emailExists={emailExists}
            onStepChange={onStepChange}
            onFormComplete={onFormComplete}
            onCheckoutComplete={onCheckoutComplete}
            giftSuccessModalProps={{ onClose: onCloseGiftSuccessModal }}
            footerClassName="signup-layout-sticky-footer"
            onFirstStepBack={() => history.goBack()}
            renderTitle={({ activeStep }) => {
              const title = (() => {
                switch (activeStep.key) {
                  case FREEMIUM_EVENT_STEPS.DETAILS:
                    return (
                      <h2>
                        Tell us more about the wedding so we can tailor the
                        experience!
                      </h2>
                    );
                  case FREEMIUM_EVENT_STEPS.SKU_SELECTION:
                    return (
                      <h2>
                        Select all the items you&apos;d like to gift the lucky
                        couple!
                      </h2>
                    );
                  case FREEMIUM_EVENT_STEPS.WALDOFICATION:
                    return (
                      <h2>
                        Would you like to upgrade the digital photo gallery to{' '}
                        <span className="text-gold">Gold</span>?
                      </h2>
                    );
                  case FREEMIUM_EVENT_STEPS.ALBUMS:
                    return (
                      <h2>Which additional albums would you like to create?</h2>
                    );
                  case FREEMIUM_EVENT_STEPS.JOIN_CODE:
                    return <h2>Create a unique join code for the guests!</h2>;
                  case FREEMIUM_EVENT_STEPS.SOCIAL:
                    return (
                      <h2>
                        Add a custom photo filter and hashtag for social shares
                      </h2>
                    );
                  case FREEMIUM_EVENT_STEPS.REVIEW:
                    return (
                      <div className="mb-2">
                        <h2 className="mb-0">
                          Review the selected settings settings
                        </h2>
                        <span className="text-light text-sm">
                          The lucky couple will be able to update this later
                        </span>{' '}
                      </div>
                    );
                  default:
                    return null;
                }
              })();

              return <>{title}</>;
            }}
          />
        </div>
      </SignupAlbumLayout.Wrapper>
    </Fragment>
  );
};

WeddingsGiftConfigure.propTypes = propTypes;

export default WeddingsGiftConfigure;
